import { ComponentProps, ReactNode, useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { colors, hexToRgbA } from '@karnott/colors';
import { SaveIcon } from '@karnott/icons';
import { fontFamily, msDuration, pixelSize, pixelSpacing, spacing } from '@karnott/theme';
import { Tooltip, TooltipMessage } from '@karnott/tooltip';

const CheckboxesContainer = styled.div<{
  vertical?: boolean;
  space?: number;
}>`
  display: flex;
  flex-direction: ${({ vertical }) => (vertical ? 'column' : 'row')};
  align-items: flex-start;
  gap: ${({ space }) => space}px;
`;

const CheckboxWrapper = styled.label<{
  checked: boolean;
  $color?: string;
  checkedColor?: string;
  $disabled?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: ${pixelSpacing('small')};
  user-select: none;
  color: ${(props) =>
    props.$disabled
      ? props.$color
        ? hexToRgbA(props.$color, 0.6)
        : props.checked
          ? colors('black')
          : colors('grey')
      : props.$color || (props.checked ? colors('black', 600) : colors('black'))};
  transition: all ${msDuration('short')} ease-in-out;
  font-family: ${fontFamily()};
  font-size: ${pixelSize()};

  [type='checkbox'] {
    display: none;
  }

  ${({ $disabled, $color, checkedColor }) =>
    $disabled
      ? css`
          cursor: not-allowed;
        `
      : css`
          cursor: pointer;

          &:hover {
            color: ${$color || colors('black', 600)};
          }

          &:hover .sdk-checkbox-wrapper {
            box-shadow: inset 0 0 0 1px ${checkedColor || colors('green')};
          }
        `}
`;

type CheckboxIconWrapperProps = {
  checked: boolean;
  checkedColor?: string;
  defaultColor?: string;
  $disabled?: boolean;
};
const CheckboxIconWrapper = styled.div.attrs<CheckboxIconWrapperProps>({
  className: 'sdk-checkbox-wrapper',
})<CheckboxIconWrapperProps>`
  box-shadow: inset 0 0 0 1px ${(props) => (props.checked ? 'transparent' : props.defaultColor || colors('grey', 400))};
  background-color: ${(props) =>
    props.checked
      ? props.$disabled
        ? props.checkedColor
          ? hexToRgbA(props.checkedColor, 0.6)
          : colors('green', 400)
        : props.checkedColor || colors('green')
      : colors('white')};
  display: flex;
  flex-shrink: 0;
  align-items: center;
  border-radius: 5px;

  transition: all ${msDuration('regular')} ease-in-out;
  position: relative;

  &::after {
    // extended clickable area
    content: '';
    width: 28px;
    height: 28px;
    position: absolute;
    left: -6px;
  }
`;

const CheckboxLabel = styled.span<{
  hide: boolean;
}>`
  display: ${({ hide }) => (hide ? 'none' : 'inline')};
  transition: font-weight 0s;
`;

const CheckboxTooltip = styled.div`
  margin-left: ${pixelSpacing('small')};
`;

type Orientation = 'top' | 'right' | 'bottom' | 'left';
type TooltipProps = ComponentProps<typeof Tooltip>;

type CheckboxProps = {
  /** Whether the checkbox is in a checked state */
  checked: boolean;
  /** The color of the checked state */
  checkedColor?: string;
  /** The color of the unchecked state */
  defaultColor?: string;
  /** The color of the text */
  color?: string;
  /** The label of the checkbox */
  label?: ReactNode;
  /** The id of the checkbox. Defaults to the label */
  id?: string;
  /** Whether the label should be hidden */
  hideLabel?: boolean;
  /** Whether the checkbox is disabled */
  disabled?: boolean;
  /** The callback when the checkbox is clicked, to update its state */
  onChange: (id: string) => void;
  /** The tooltip definition to explain the option */
  tooltip?: TooltipProps;
  /** The orientation of the tooltip showing the complete label */
  hoverTextOrientation?: Orientation;
};

/** A checkbox to represent a selected or unselected option */
function Checkbox({
  checked,
  checkedColor,
  defaultColor,
  color,
  label,
  id,
  hideLabel = false,
  disabled = false,
  onChange,
  tooltip,
  hoverTextOrientation = 'top',
}: CheckboxProps) {
  const [hovered, setHovered] = useState(false);
  const pRef = useRef<HTMLDivElement>(null);
  const onOver = useCallback((state: boolean) => {
    if (pRef.current && pRef.current.scrollWidth > pRef.current.offsetWidth) {
      setHovered(state);
    }
  }, []);

  const checkboxId = id || (typeof label === 'string' ? label : undefined);

  return (
    <CheckboxWrapper
      onClick={(e) => e.stopPropagation()}
      onFocus={() => onOver(true)}
      onMouseOver={() => onOver(true)}
      onMouseLeave={() => onOver(false)}
      htmlFor={checkboxId}
      checkedColor={checkedColor}
      checked={checked}
      $color={color}
      $disabled={disabled}
      data-testid="sdk-checkbox-container"
    >
      <input
        type="checkbox"
        id={checkboxId}
        checked={checked}
        onChange={() => onChange(checkboxId || '')}
        disabled={disabled}
      />

      <CheckboxIconWrapper
        checked={checked}
        checkedColor={checkedColor}
        defaultColor={defaultColor}
        role="checkbox"
        aria-checked={checked}
        $disabled={disabled}
      >
        <SaveIcon size={15} circled backgroundColor="transparent" color={colors('white')} />
      </CheckboxIconWrapper>

      {hideLabel || !label ? null : (
        <div ref={pRef}>
          {hovered && typeof label === 'string' && (
            <TooltipMessage orientation={hoverTextOrientation} message={label} />
          )}
          <CheckboxLabel hide={hideLabel}>{label}</CheckboxLabel>
        </div>
      )}

      {tooltip ? (
        <CheckboxTooltip>
          <Tooltip {...tooltip} />
        </CheckboxTooltip>
      ) : null}
    </CheckboxWrapper>
  );
}

type CheckboxesProps = {
  /** The color of the checked state */
  checkedColor?: string;
  /** The color of the unchecked state */
  defaultColor?: string;
  /** The labels of the checkboxes */
  items: string[];
  /** The checked labels */
  selection: string[];
  /** Callback when a checkbox is clicked, to update the state */
  onChange: (id: string) => void;
  /** One tooltip definition per checkbox */
  tooltips?: TooltipProps[];
  /** Whether the checkboxes should be displayed horizontally (default: vertically) */
  horizontal?: boolean;
  /** The space between each option */
  space?: number;
  /** Whether the checkboxes are disabled */
  disabled?: boolean;
};

/** A group of checkboxes */
function Checkboxes({
  checkedColor,
  defaultColor,
  items,
  selection,
  onChange,
  tooltips = [],
  horizontal = false,
  space = spacing('xSmall'),
  disabled = false,
}: CheckboxesProps) {
  return (
    <CheckboxesContainer vertical={!horizontal} space={space}>
      {items.map((item, index) => {
        const tooltip = tooltips[index];
        return (
          <Checkbox
            key={`${item}-${index}`}
            checked={selection.includes(item)}
            label={item}
            onChange={onChange}
            tooltip={tooltip}
            checkedColor={checkedColor}
            defaultColor={defaultColor}
            disabled={disabled}
          />
        );
      })}
    </CheckboxesContainer>
  );
}

export { Checkbox, Checkboxes };
